/**
 * Flash Message Context
 * Created: 2024-03-27
 * Last Updated: 2024-03-27
 *
 * Provides global flash message functionality for notifications and alerts.
 *
 * Changes:
 * - Added convenience methods for different message types
 * - Added proper TypeScript types for all methods
 * - Exported FlashMessageContextType interface
 */

'use client'

import React, {createContext, useCallback, useContext, useEffect, useState} from 'react'

export type FlashMessageType = 'success' | 'error' | 'info' | 'warning'

export interface FlashMessage {
  id: string
  type: FlashMessageType
  message: string
  duration?: number
  autoClose?: boolean
}

export interface FlashMessageContextType {
  messages: FlashMessage[]
  addMessage: (message: Omit<FlashMessage, 'id'>) => void
  removeMessage: (id: string) => void
  clearMessages: () => void
  showError: (message: string, duration?: number) => void
  showSuccess: (message: string, duration?: number) => void
  showInfo: (message: string, duration?: number) => void
  showWarning: (message: string, duration?: number) => void
}

const FlashMessageContext = createContext<FlashMessageContextType | undefined>(undefined)

interface FlashMessageProviderProps {
  children: React.ReactNode
}

export const FlashMessageProvider: React.FC<FlashMessageProviderProps> = ({children}) => {
  const [messages, setMessages] = useState<FlashMessage[]>([])

  const removeMessage = useCallback((id: string) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id))
    console.debug('Removed flash message', {messageId: id})
  }, [])

  const addMessage = useCallback(
    ({type, message, duration = 5000, autoClose = true}: Omit<FlashMessage, 'id'>) => {
      const id = Math.random().toString(36).substring(7)
      const newMessage: FlashMessage = {
        id,
        type,
        message,
        duration,
        autoClose
      }

      setMessages((prev) => [...prev, newMessage])
      console.debug('Added flash message', {type, message})

      if (autoClose) {
        setTimeout(() => {
          removeMessage(id)
        }, duration)
      }
    },
    [removeMessage]
  )

  const clearMessages = useCallback(() => {
    setMessages([])
    console.debug('Cleared all flash messages')
  }, [])

  // Convenience methods for different message types
  const showError = useCallback(
    (message: string, duration?: number) => {
      addMessage({type: 'error', message, duration})
    },
    [addMessage]
  )

  const showSuccess = useCallback(
    (message: string, duration?: number) => {
      addMessage({type: 'success', message, duration})
    },
    [addMessage]
  )

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      addMessage({type: 'info', message, duration})
    },
    [addMessage]
  )

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      addMessage({type: 'warning', message, duration})
    },
    [addMessage]
  )

  // Clean up messages on unmount
  useEffect(() => {
    return () => {
      clearMessages()
    }
  }, [clearMessages])

  return (
    <FlashMessageContext.Provider
      value={{
        messages,
        addMessage,
        removeMessage,
        clearMessages,
        showError,
        showSuccess,
        showInfo,
        showWarning
      }}>
      {children}
    </FlashMessageContext.Provider>
  )
}

export const useFlashMessage = () => {
  const context = useContext(FlashMessageContext)
  if (!context) {
    throw new Error('useFlashMessage must be used within a FlashMessageProvider')
  }
  return context
}
